import { useCallback, useEffect, useState } from 'react';
import { useMagic } from '@package/smart-wallet/providers';
import * as Sentry from '@sentry/react';

export const useHandleMFA = () => {
  const [isGettingMFAStatus, setIsGettingMFAStatus] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMFAEnabled, setIsMFAEnabled] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { magic } = useMagic();

  /* Callbacks */
  const handleEnableMFA = useCallback(async () => {
    setIsLoading(true);
    try {
      await magic?.user.enableMFA({
        showUI: true,
      });
    } catch (error: any) {
      console.error(`Error enabling MFA: ${error}`);
      setError(`Error enabling MFA: ${error?.response?.data?.msg}`);
      Sentry.captureException({
        message: 'Error enabling MFA',
        extra: {
          error,
        },
      });
    } finally {
      setIsLoading(false);
    }
  }, [magic]);

  const handleDisableMFA = useCallback(async () => {
    setIsLoading(true);
    try {
      await magic?.user.disableMFA({
        showUI: true,
      });
    } catch (error: any) {
      console.error(`Error disabling MFA: ${error}`);
      setError(`Error disabling MFA: ${error?.response?.data?.msg}`);
      Sentry.captureException({
        message: 'Error disabling MFA',
        extra: {
          error,
        },
      });
    } finally {
      setIsLoading(false);
    }
  }, [magic]);

  /* Effects */
  useEffect(() => {
    const get2faStatus = async () => {
      setIsGettingMFAStatus(true);
      try {
        const user = await magic?.user.getInfo();
        if (user) {
          setIsMFAEnabled(user?.isMfaEnabled);
        }
      } catch (error: any) {
        console.error(`Error getting MFA status: ${error}`);
        setError(`Error getting MFA status: ${error?.response?.data?.msg}`);
        Sentry.captureException({
          message: 'Error getting MFA status',
          extra: {
            error,
          },
        });
      } finally {
        setIsGettingMFAStatus(false);
      }
    };

    get2faStatus();
  }, [magic]);

  /* Return */
  return {
    error,
    isLoading,
    isGettingMFAStatus,
    isMFAEnabled,
    handleEnableMFA,
    handleDisableMFA,
  };
};
